import React, { useEffect, useMemo, useState } from 'react'
import * as S from './ArtboardDetailInspectorOverlay.styles'
import {
  PRMarinaNodeIdentifier,
  PRRect,
  useIsCameraMoving,
} from '@sketch-hq/sketch-web-renderer'
import { SelectionGuidelines } from './SelectionGuidelines'
import { HoveredLayerOverlay } from './HoveredLayerOverlay'
import { MeasurementLine } from './MeasurementLine'
import { Orientation } from './types'
import {
  InspectorLayersTreeArtboardLayerNode,
  useInspectorHoveredNode,
  useInspectorContext,
  useMapPRRectToCanvasRelativeRect,
} from '../../hooks'
import { SymbolInstanceOverlay } from './SymbolInstanceOverlay/SymbolInstanceOverlay'
import { useMeasurementData } from './useMeasurementData'
import { OverlayGrid } from './OverlayGrid'
import { OverlayLayout } from './OverlayLayout'
import { useArtboardDetailContext } from '../../hooks/useArtboardDetailContext'
import { isArtboard } from '../../hooks/inspector/sketchSceneLib/inspectorTreeNodeUtilsDeprecated'

type InspectorProps = {
  canvasContainerRef: React.RefObject<HTMLDivElement>
}

export function ArtboardDetailInspectorOverlay({
  canvasContainerRef,
}: InspectorProps) {
  const isMoving = useIsCameraMoving()
  const { selectedNode, layersTreeRootNode } = useInspectorContext()
  const hoveredNode = useInspectorHoveredNode()

  const measurementData = useMeasurementData(selectedNode, hoveredNode)

  const [selectionData, setSelectionData] = useState<{
    bounds: PRRect
    identifier: PRMarinaNodeIdentifier
  } | null>(null)

  /**
   * When we select a layer that is inside a symbol instance, we want to also
   * display the symbol layer (in purple) alongside of the selected layer (in red).
   */
  const symbolInstanceData = useMemo(() => {
    if (!selectedNode || isArtboard(selectedNode)) {
      return null
    }

    // If the currently selected node is a symbol instance use it,
    // otherwise check if it belongs to a symbol.
    const symbolLayerNode =
      selectedNode.type === 'symbolInstance'
        ? selectedNode
        : selectedNode?.parentSymbolLayer

    if (symbolLayerNode) {
      return {
        identifier: symbolLayerNode?.prMarinaNode.getIdentifier(),
        bounds: symbolLayerNode?.prMarinaNode.getAbsoluteBounds(),
        symbolMaster: symbolLayerNode.symbolMaster,
      }
    }

    return null
  }, [selectedNode])

  useEffect(() => {
    // Don't show selection of the artboard
    if (selectedNode == null || isArtboard(selectedNode)) {
      setSelectionData(null)
      return
    }

    setSelectionData({
      identifier: selectedNode.prMarinaNode.getIdentifier(),
      bounds: selectedNode.prMarinaNode.getAbsoluteBounds(),
    })
  }, [selectedNode])

  const hoveredNodeBounds = useMemo(() => {
    // Don't show hover on the artboard
    const isArtboardLayer = hoveredNode && isArtboard(hoveredNode)
    // Don't show hover rectangle on top of selected layer that already has a red rectangle.
    const isSelectedNode = selectedNode?.identifier === hoveredNode?.identifier

    if (!hoveredNode || isArtboardLayer || isSelectedNode) {
      return null
    }

    return hoveredNode?.prMarinaNode.getAbsoluteBounds()
  }, [hoveredNode, selectedNode?.identifier])

  // Hide the inspector when the camera is moving
  // We don't want to cause frame drops when the camera is moving by re-rendering React components
  if (isMoving) return null

  const artboardNode =
    layersTreeRootNode && isArtboard(layersTreeRootNode)
      ? layersTreeRootNode
      : undefined
  return (
    <S.Container>
      {artboardNode && <GridAndLayout artboardNode={artboardNode} />}
      {measurementData?.horizontalLine && (
        <MeasurementLine
          line={measurementData.horizontalLine}
          orientation={Orientation.Horizontal}
        />
      )}

      {measurementData?.secondHorizontalLine && (
        <MeasurementLine
          line={measurementData.secondHorizontalLine}
          orientation={Orientation.Horizontal}
        />
      )}

      {measurementData?.verticalLine && (
        <MeasurementLine
          line={measurementData.verticalLine}
          orientation={Orientation.Vertical}
        />
      )}

      {measurementData?.secondVerticalLine && (
        <MeasurementLine
          line={measurementData.secondVerticalLine}
          orientation={Orientation.Vertical}
        />
      )}

      {hoveredNodeBounds && <HoveredLayerOverlay bounds={hoveredNodeBounds} />}

      {selectionData && <SelectionGuidelines bounds={selectionData.bounds} />}

      {symbolInstanceData && (
        <SymbolInstanceOverlay
          bounds={symbolInstanceData.bounds}
          symbolMaster={symbolInstanceData.symbolMaster}
          canvasContainerRef={canvasContainerRef}
        />
      )}
    </S.Container>
  )
}

type GridAndLayoutProps = {
  artboardNode: InspectorLayersTreeArtboardLayerNode
}
function GridAndLayout({ artboardNode }: GridAndLayoutProps) {
  const { isGridEnabled, isLayoutEnabled } = useArtboardDetailContext()
  const artboardBounds = artboardNode.prMarinaNode.getAbsoluteBounds()
  const artboardBoundsRelative = useMapPRRectToCanvasRelativeRect(
    artboardBounds
  )
  const gridSettings = artboardNode.gridSettings
  const layoutSettings = artboardNode.layoutSettings

  if (!artboardBoundsRelative || (!gridSettings && !layoutSettings)) {
    return null
  }

  return (
    <>
      {artboardBounds && (
        // Create a container positioned exactly on top of the artboard.
        // The grid and the layout will be drawn inside this container.
        <S.ArtboardBoundsContainer
          x={artboardBoundsRelative.x}
          y={artboardBoundsRelative.y}
          width={artboardBoundsRelative.width}
          height={artboardBoundsRelative.height}
        >
          {gridSettings && (
            <OverlayGrid
              isEnabled={isGridEnabled}
              gridSize={gridSettings.size}
              thickLineStep={gridSettings.thickLineStep}
            />
          )}
          {layoutSettings && (
            <OverlayLayout
              isEnabled={isLayoutEnabled}
              layoutSettings={layoutSettings}
              artboardWidth={artboardBoundsRelative.width}
              artboardHeight={artboardBoundsRelative.height}
            />
          )}
        </S.ArtboardBoundsContainer>
      )}
    </>
  )
}
